import { ChangeEvent, useLayoutEffect, useReducer, useRef } from "react";
import { caretAfterDigits, significantBefore } from "../utils/displayMask";

type MaskedInputOptions = {
  // The clean value held in state: digits, and a decimal point where allowed.
  value: string;
  clean: (raw: string) => string;
  display: (value: string) => string;
  onValueChange: (value: string) => void;
};

// Shows `value` through a display mask while state keeps the clean value, and
// puts the caret back after the same digit once the field is reformatted.
const useMaskedInput = ({ value, clean, display, onValueChange }: MaskedInputOptions) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const pendingDigits = useRef<number | null>(null);
  // Deleting only a separator leaves the clean value unchanged; the forced
  // render still runs the effect, so the caret is placed instead of jumping.
  const [, rerender] = useReducer((count: number) => count + 1, 0);
  const shown = display(value);

  useLayoutEffect(() => {
    const input = inputRef.current;
    if (pendingDigits.current === null || !input) return;

    const position = caretAfterDigits(shown, pendingDigits.current);
    pendingDigits.current = null;
    if (input.ownerDocument.activeElement === input) {
      input.setSelectionRange(position, position);
    }
  });

  const onChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const raw = event.target.value;
    const next = clean(raw);
    const before = significantBefore(raw, event.target.selectionStart ?? raw.length);

    pendingDigits.current = Math.min(before, next.length);
    onValueChange(next);
    rerender();
  };

  return { inputRef, value: shown, onChange };
};

export default useMaskedInput;
